import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { biAPI } from '../../services/api';
import FilterSelect from '../../components/common/FilterSelect';
import BiFilters from './BiFilters';
import { biPanel, chartAxisTick, chartTooltipStyle, formatNpr, formatNumber, formatPct } from './biTheme';
import ScrollableBarList from './ScrollableBarList';
import { useBiVendorId } from './useBiVendorId';
import useDebouncedValue from './useDebouncedValue';

const defaultTo = () => new Date().toISOString().slice(0, 10);
const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return d.toISOString().slice(0, 10);
};

const productRowKey = (row) => `${row.productId}-${row.categoryId}-${row.enteredOn}`;

const ALL_FILTER = '__all__';

const toFilterOptions = (values, allLabel) => [
  { value: ALL_FILTER, label: allLabel },
  ...values.map((name) => ({ value: name, label: name })),
];

const PromoTracker = () => {
  const { vendorId } = useBiVendorId();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [weekdays, setWeekdays] = useState([]);
  const [scope, setScope] = useState('overall');
  const [campaignFilter, setCampaignFilter] = useState(ALL_FILTER);
  const [parentCategoryFilter, setParentCategoryFilter] = useState(ALL_FILTER);
  const [productNameFilter, setProductNameFilter] = useState(ALL_FILTER);
  const [dailyCampaignFilter, setDailyCampaignFilter] = useState(ALL_FILTER);
  const debouncedFrom = useDebouncedValue(from, 450);
  const debouncedTo = useDebouncedValue(to, 450);

  const queryParams = useMemo(() => ({
    vendorId,
    from: debouncedFrom,
    to: debouncedTo,
    includeDaily: 1,
  }), [vendorId, debouncedFrom, debouncedTo]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    ['bi-promo', queryParams],
    async () => {
      const response = await biAPI.getPromo(queryParams);
      return response.data?.data || response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const products = useMemo(() => {
    if (scope === 'live') return data?.liveProducts || data?.products?.filter((p) => p.isLive) || [];
    return data?.overallProducts || data?.products || [];
  }, [data, scope]);

  const campaignOptions = useMemo(() => {
    const names = [...new Set(products.map((row) => row.campaignName).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [products]);

  const parentCategoryOptions = useMemo(() => {
    const scoped = campaignFilter === ALL_FILTER
      ? products
      : products.filter((row) => row.campaignName === campaignFilter);
    const names = [...new Set(scoped.map((row) => row.parentCategoryName).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [products, campaignFilter]);

  const productNameOptions = useMemo(() => {
    let scoped = products;
    if (campaignFilter !== ALL_FILTER) {
      scoped = scoped.filter((row) => row.campaignName === campaignFilter);
    }
    if (parentCategoryFilter !== ALL_FILTER) {
      scoped = scoped.filter((row) => row.parentCategoryName === parentCategoryFilter);
    }
    const names = [...new Set(scoped.map((row) => row.productName).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [products, campaignFilter, parentCategoryFilter]);

  const campaignFilterOptions = useMemo(
    () => toFilterOptions(campaignOptions, 'All campaigns'),
    [campaignOptions]
  );

  const parentCategoryFilterOptions = useMemo(
    () => toFilterOptions(parentCategoryOptions, 'All parent categories'),
    [parentCategoryOptions]
  );

  const productNameFilterOptions = useMemo(
    () => toFilterOptions(productNameOptions, 'All products'),
    [productNameOptions]
  );

  const dailyCampaignFilterOptions = useMemo(
    () => toFilterOptions(campaignOptions, 'All filtered campaigns'),
    [campaignOptions]
  );

  const filteredProducts = useMemo(() => products.filter((row) => {
    if (campaignFilter !== ALL_FILTER && row.campaignName !== campaignFilter) return false;
    if (parentCategoryFilter !== ALL_FILTER && row.parentCategoryName !== parentCategoryFilter) return false;
    if (productNameFilter !== ALL_FILTER && row.productName !== productNameFilter) return false;
    return true;
  }), [products, campaignFilter, parentCategoryFilter, productNameFilter]);

  useEffect(() => {
    if (campaignFilter !== ALL_FILTER && !campaignOptions.includes(campaignFilter)) {
      setCampaignFilter(ALL_FILTER);
    }
  }, [campaignFilter, campaignOptions]);

  useEffect(() => {
    if (parentCategoryFilter !== ALL_FILTER && !parentCategoryOptions.includes(parentCategoryFilter)) {
      setParentCategoryFilter(ALL_FILTER);
    }
  }, [parentCategoryFilter, parentCategoryOptions]);

  useEffect(() => {
    if (productNameFilter !== ALL_FILTER && !productNameOptions.includes(productNameFilter)) {
      setProductNameFilter(ALL_FILTER);
    }
  }, [productNameFilter, productNameOptions]);

  useEffect(() => {
    if (dailyCampaignFilter !== ALL_FILTER && !campaignOptions.includes(dailyCampaignFilter)) {
      setDailyCampaignFilter(campaignOptions[0] || ALL_FILTER);
    }
  }, [dailyCampaignFilter, campaignOptions]);

  const summary = scope === 'live' ? (data?.liveSummary || data?.summary) : data?.summary;
  const prevPeriod = data?.prevPeriod || {};

  const productBarRows = useMemo(
    () => {
      const byName = new Map();
      filteredProducts.forEach((row) => {
        const name = String(row.productName || `SKU ${row.productId}`).trim();
        const key = name.toLowerCase();
        if (!byName.has(key)) {
          byName.set(key, { name, value: 0 });
        }
        byName.get(key).value += Math.round(row.actualOrders || 0);
      });
      return [...byName.values()]
        .sort((a, b) => b.value - a.value)
        .slice(0, 12)
        .map((row) => ({ name: row.name, value: row.value }));
    },
    [filteredProducts]
  );

  const categoryBarRows = useMemo(
    () => {
      const byName = new Map();
      filteredProducts.forEach((row) => {
        const name = String(row.categoryName || 'Category').trim();
        const key = name.toLowerCase();
        if (!byName.has(key)) {
          byName.set(key, { name, value: 0 });
        }
        byName.get(key).value += Math.round(row.actualOrders || 0);
      });
      return [...byName.values()]
        .sort((a, b) => b.value - a.value)
        .slice(0, 12)
        .map((row) => ({ name: row.name, value: row.value }));
    },
    [filteredProducts]
  );

  const categoriesBySalesRows = useMemo(
    () => {
      const byName = new Map();
      filteredProducts.forEach((row) => {
        const name = String(row.categoryName || 'Category').trim();
        const key = name.toLowerCase();
        if (!byName.has(key)) {
          byName.set(key, { name, value: 0 });
        }
        byName.get(key).value += Math.round(Number(row.actualSales || 0));
      });
      return [...byName.values()]
        .sort((a, b) => b.value - a.value)
        .slice(0, 12)
        .map((row) => ({ name: row.name, value: row.value }));
    },
    [filteredProducts]
  );

  const dailyCampaignProducts = useMemo(() => {
    if (dailyCampaignFilter === ALL_FILTER) return filteredProducts;
    return filteredProducts.filter((row) => row.campaignName === dailyCampaignFilter);
  }, [filteredProducts, dailyCampaignFilter]);

  const dailySummary = useMemo(() => {
    const totals = dailyCampaignProducts.reduce((acc, row) => ({
      actualOrders: acc.actualOrders + Number(row.actualOrders || 0),
      prevPeriodOrders: acc.prevPeriodOrders + Number(row.prevPeriodOrders || 0),
      upliftOrders: acc.upliftOrders + Number(row.upliftOrders || 0),
      liveCount: acc.liveCount + (row.isLive ? 1 : 0),
    }), { actualOrders: 0, prevPeriodOrders: 0, upliftOrders: 0, liveCount: 0 });
    const upliftOrdersPct = totals.prevPeriodOrders > 0
      ? (totals.upliftOrders / totals.prevPeriodOrders) * 100
      : 0;
    return { ...totals, upliftOrdersPct };
  }, [dailyCampaignProducts]);

  const weekdaySet = useMemo(() => new Set(weekdays), [weekdays]);

  const dailyRows = useMemo(() => {
    const weekdayName = (dateKey) => {
      const d = new Date(`${dateKey}T00:00:00Z`);
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getUTCDay()];
    };
    const byDate = new Map();
    dailyCampaignProducts.forEach((product) => {
      (product.daily || []).forEach((row) => {
        const dateKey = String(row.date).slice(0, 10);
        if (weekdaySet.size && !weekdaySet.has(weekdayName(dateKey))) return;
        const existing = byDate.get(dateKey) || {
          orders: 0,
          prevPeriod: 0,
          uplift: 0,
        };
        byDate.set(dateKey, {
          orders: existing.orders + Number(row.orders || 0),
          prevPeriod: existing.prevPeriod + Number(row.prevPeriodOrders || 0),
          uplift: existing.uplift + Number(row.upliftOrders || 0),
        });
      });
    });
    return [...byDate.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([dateKey, row]) => ({
        date: dateKey.slice(5),
        orders: row.orders,
        prevPeriod: row.prevPeriod,
        uplift: row.uplift,
      }));
  }, [dailyCampaignProducts, weekdaySet]);

  const gainers = useMemo(
    () => [...filteredProducts].filter((p) => (p.upliftOrders || 0) > 0).sort((a, b) => b.upliftOrders - a.upliftOrders).slice(0, 5),
    [filteredProducts]
  );
  const underperformers = useMemo(
    () => [...filteredProducts].filter((p) => (p.upliftOrders || 0) < 0).sort((a, b) => a.upliftOrders - b.upliftOrders).slice(0, 5),
    [filteredProducts]
  );

  const historyArchive = useMemo(
    () => (data?.historyArchive || []).filter((row) => {
      if (campaignFilter !== ALL_FILTER && row.campaignName !== campaignFilter) return false;
      if (parentCategoryFilter !== ALL_FILTER && row.parentCategoryName !== parentCategoryFilter) return false;
      if (productNameFilter !== ALL_FILTER && row.productName !== productNameFilter) return false;
      return true;
    }),
    [data?.historyArchive, campaignFilter, parentCategoryFilter, productNameFilter]
  );

  const downloadHistoryCsv = () => {
    const header = [
      'ProductId',
      'ProductName',
      'Campaign',
      'Category',
      'ParentCategory',
      'EnteredOn',
      'ExitedOn',
      'MirrorDays',
      'Sales',
      'Orders',
      'OosDays',
      'UpliftOrders',
      'UpliftSales',
    ];
    const rows = historyArchive.map((row) => [
      row.productId,
      `"${String(row.productName || '').replace(/"/g, '""')}"`,
      `"${String(row.campaignName || '').replace(/"/g, '""')}"`,
      `"${String(row.categoryName || '').replace(/"/g, '""')}"`,
      `"${String(row.parentCategoryName || '').replace(/"/g, '""')}"`,
      row.enteredOn || '',
      row.exitedOn || '',
      row.mirrorDays || 0,
      Math.round(row.sales || 0),
      Math.round(row.orders || 0),
      row.oosDays || 0,
      Math.round(row.upliftOrders || 0),
      Math.round(row.upliftSales || 0),
    ]);
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `promo-deals-exit-archive-${data?.startDate || 'range'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Promo Deals</h2>
          {data?.startDate ? (
            <p className="mt-1 text-sm text-slate-600">
              Campaign · {data.startDate} → {data.endDate}
              {prevPeriod.startDate ? ` · vs ${prevPeriod.startDate} → ${prevPeriod.endDate}` : ''}
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-600">Campaign categories</p>
          )}
        </div>
        <BiFilters
          inline
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          weekdays={weekdays}
          onWeekdaysChange={setWeekdays}
          onRefresh={() => refetch()}
          refreshing={isFetching}
        >
          <FilterSelect
            label="Campaign"
            value={campaignFilter}
            options={campaignFilterOptions}
            onChange={(nextValue) => {
              setCampaignFilter(nextValue);
              setParentCategoryFilter(ALL_FILTER);
              setProductNameFilter(ALL_FILTER);
            }}
            className="w-[168px]"
          />
          <FilterSelect
            label="Parent category"
            value={parentCategoryFilter}
            options={parentCategoryFilterOptions}
            onChange={(nextValue) => {
              setParentCategoryFilter(nextValue);
              setProductNameFilter(ALL_FILTER);
            }}
            className="w-[188px]"
          />
          <FilterSelect
            label="Product"
            value={productNameFilter}
            options={productNameFilterOptions}
            onChange={setProductNameFilter}
            className="min-w-[200px] max-w-[248px]"
          />
          <div className="flex self-end overflow-hidden rounded-lg border border-slate-200">
            <button
              type="button"
              onClick={() => setScope('overall')}
              className={`px-3 py-2 text-sm font-semibold ${scope === 'overall' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}
            >
              Overall
            </button>
            <button
              type="button"
              onClick={() => setScope('live')}
              className={`px-3 py-2 text-sm font-semibold ${scope === 'live' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600'}`}
            >
              Live
            </button>
          </div>
        </BiFilters>
      </div>

      {isLoading ? <div className="card text-gray-500">Loading…</div> : null}
      {isError ? (
        <div className="card border-rose-200 bg-rose-50 text-rose-700">
          {error?.response?.data?.message || error?.message || 'Failed to load'}
        </div>
      ) : null}

      {summary ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-4">
            <p className="text-sm uppercase text-cyan-700">Actual orders</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(Math.round(summary.actualOrders || 0))}</p>
          </div>
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
            <p className="text-sm uppercase text-violet-700">Prev period orders</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(Math.round(summary.prevPeriodOrders || 0))}</p>
            {prevPeriod.startDate ? (
              <p className="text-sm text-gray-600">{prevPeriod.startDate} → {prevPeriod.endDate}</p>
            ) : null}
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm uppercase text-emerald-700">Orders uplift</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(Math.round(summary.upliftOrders || 0))}</p>
            <p className="text-sm text-gray-600">{formatPct(summary.upliftOrdersPct)}</p>
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
            <p className="text-sm uppercase text-sky-700">Sales uplift</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{formatNpr(summary.upliftSales)}</p>
            <p className="text-sm text-gray-600">{formatPct(summary.upliftSalesPct)}</p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm uppercase text-amber-700">SKUs</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(summary.productCount || 0)}</p>
            <p className="text-sm text-gray-600">Live {formatNumber(summary.liveCount || 0)}</p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-3">
        <ScrollableBarList
          variant="card"
          theme="blue"
          title="Products by orders"
          footerLabel="Orders"
          footerStrong="Top products"
          rows={productBarRows}
          formatValue={(v) => formatNumber(v)}
          emptyText="No promo products in this range."
          maxVisible={8}
        />
        <ScrollableBarList
          variant="card"
          theme="green"
          title="Categories by orders"
          footerLabel="Orders"
          footerStrong="Top category"
          rows={categoryBarRows}
          formatValue={(v) => formatNumber(v)}
          emptyText="No categories in this range."
          maxVisible={8}
        />
        <ScrollableBarList
          variant="card"
          theme="purple"
          title="Categories by sales"
          footerLabel="Sales"
          footerStrong="Top category"
          rows={categoriesBySalesRows}
          formatValue={(v) => formatNpr(v)}
          emptyText="No campaign sales in this range."
          maxVisible={8}
        />
      </div>

      <div className={biPanel}>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Daily</h3>
            <p className="text-[11px] text-slate-500">Campaign-level orders vs previous period</p>
          </div>
          <FilterSelect
            label="Campaign"
            value={dailyCampaignFilter}
            options={dailyCampaignFilterOptions}
            onChange={setDailyCampaignFilter}
            className="w-[188px]"
          />
        </div>

        {dailyCampaignProducts.length ? (
          <>
            <div className="mb-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                <p className="text-slate-500">Products</p>
                <p className="font-semibold text-slate-800">{formatNumber(dailyCampaignProducts.length)}</p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                <p className="text-slate-500">Prev period</p>
                <p className="font-semibold text-slate-800">{formatNumber(Math.round(dailySummary.prevPeriodOrders || 0))}</p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                <p className="text-slate-500">Actual</p>
                <p className="font-semibold text-slate-800">{formatNumber(Math.round(dailySummary.actualOrders || 0))}</p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                <p className="text-slate-500">Uplift</p>
                <p className={`font-semibold ${(dailySummary.upliftOrders || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {formatNumber(Math.round(dailySummary.upliftOrders || 0))} ({formatPct(dailySummary.upliftOrdersPct)})
                </p>
              </div>
            </div>

            <div className="h-72">
              {dailyRows.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dailyRows}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" tick={chartAxisTick} />
                    <YAxis stroke="#64748b" tick={chartAxisTick} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Legend />
                    <Bar dataKey="orders" name="Orders" fill="#38bdf8" radius={[3, 3, 0, 0]} />
                    <Line type="monotone" dataKey="prevPeriod" name="Prev period" stroke="#7c3aed" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="uplift" name="Uplift" stroke="#10b981" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-500">—</p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">No products match the current filters.</p>
        )}
      </div>

      <div className={biPanel}>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Product mirror performance</h3>
          <p className="text-[11px] text-slate-500">
            Respects Overall / Live toggle and page filters
          </p>
        </div>
        <div className="bi-slim-scroll max-h-[340px] overflow-auto rounded-lg border border-slate-100">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500 shadow-[0_1px_0_#eef1f5]">
              <tr>
                <th className="px-2 py-2">Campaign</th>
                <th className="px-2 py-2">Product</th>
                <th className="px-2 py-2">Parent category</th>
                <th className="px-2 py-2">Category</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Entered</th>
                <th className="px-2 py-2">Exited</th>
                <th className="px-2 py-2">Mirror days</th>
                <th className="px-2 py-2">Sales</th>
                <th className="px-2 py-2">Orders</th>
                <th className="px-2 py-2">OOS days</th>
                <th className="px-2 py-2">Uplift</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((row) => (
                <tr
                  key={productRowKey(row)}
                  className="border-t border-slate-100"
                >
                  <td className="px-2 py-2 text-gray-600">{row.campaignName || '—'}</td>
                  <td className="px-2 py-2 font-medium text-gray-900">{row.productName}</td>
                  <td className="px-2 py-2 text-gray-600">{row.parentCategoryName || '—'}</td>
                  <td className="px-2 py-2 text-gray-600">{row.categoryName || '—'}</td>
                  <td className="px-2 py-2">
                    <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                      row.isLive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                    }`}
                    >
                      {row.isLive ? 'Live' : 'Ended'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-gray-600">{row.enteredOn}</td>
                  <td className="px-2 py-2 text-gray-600">{row.exitedOn || '—'}</td>
                  <td className="px-2 py-2 tabular-nums text-gray-700">{formatNumber(row.mirrorDays || 0)}</td>
                  <td className="px-2 py-2 tabular-nums text-gray-700">{formatNpr(row.mirrorSales || 0)}</td>
                  <td className="px-2 py-2 tabular-nums text-gray-700">{formatNumber(Math.round(row.mirrorOrders || 0))}</td>
                  <td className="px-2 py-2 tabular-nums font-semibold text-rose-700">{formatNumber(row.oosDays || 0)}</td>
                  <td className={`px-2 py-2 font-medium ${(row.upliftOrders || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatNumber(Math.round(row.upliftOrders || 0))} ({formatPct(row.upliftOrdersPct)})
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredProducts.length ? <p className="p-3 text-sm text-gray-500">—</p> : null}
        </div>
      </div>

      <div className={biPanel}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Promo exit archive</h3>
            <p className="text-[11px] text-slate-500">
              Frozen summary after a product leaves a campaign — available anytime
            </p>
          </div>
          <button
            type="button"
            onClick={downloadHistoryCsv}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            disabled={!historyArchive.length}
          >
            Download CSV
          </button>
        </div>
        <div className="bi-slim-scroll max-h-[260px] overflow-auto rounded-lg border border-slate-100">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500 shadow-[0_1px_0_#eef1f5]">
              <tr>
                <th className="px-2 py-2">Product</th>
                <th className="px-2 py-2">Campaign</th>
                <th className="px-2 py-2">Category</th>
                <th className="px-2 py-2">Entered</th>
                <th className="px-2 py-2">Exited</th>
                <th className="px-2 py-2">Mirror days</th>
                <th className="px-2 py-2">Sales</th>
                <th className="px-2 py-2">Orders</th>
                <th className="px-2 py-2">OOS days</th>
                <th className="px-2 py-2">Uplift</th>
              </tr>
            </thead>
            <tbody>
              {historyArchive.map((row) => (
                <tr key={`${row.membershipId || row.productId}-${row.exitedOn}`} className="border-t border-slate-100">
                  <td className="px-2 py-2 font-medium text-gray-900">{row.productName}</td>
                  <td className="px-2 py-2 text-gray-600">{row.campaignName || '—'}</td>
                  <td className="px-2 py-2 text-gray-600">
                    {row.parentCategoryName}
                    {row.categoryName && row.categoryName !== row.parentCategoryName ? ` / ${row.categoryName}` : ''}
                  </td>
                  <td className="px-2 py-2 text-gray-600">{row.enteredOn}</td>
                  <td className="px-2 py-2 text-gray-600">{row.exitedOn || '—'}</td>
                  <td className="px-2 py-2 tabular-nums text-gray-700">{formatNumber(row.mirrorDays || 0)}</td>
                  <td className="px-2 py-2 tabular-nums text-gray-700">{formatNpr(row.sales || 0)}</td>
                  <td className="px-2 py-2 tabular-nums text-gray-700">{formatNumber(Math.round(row.orders || 0))}</td>
                  <td className="px-2 py-2 tabular-nums font-semibold text-rose-700">{formatNumber(row.oosDays || 0)}</td>
                  <td className={`px-2 py-2 font-medium ${(row.upliftOrders || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatNumber(Math.round(row.upliftOrders || 0))} ({formatPct(row.upliftOrdersPct)})
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!historyArchive.length ? (
            <p className="p-3 text-sm text-gray-500">No exited campaign products in this range yet.</p>
          ) : null}
        </div>
      </div>

      {filteredProducts.length ? (
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-emerald-800">Gainers</h4>
            <div className="bi-slim-scroll max-h-40 overflow-y-auto">
              {gainers.length ? (
                <ul className="space-y-2 text-sm text-emerald-950">
                  {gainers.map((p) => (
                    <li key={`${p.productId}-${p.enteredOn}`} className="flex items-start justify-between gap-2">
                      <span>{p.productName}</span>
                      <span className="shrink-0 font-semibold">
                        +{formatNumber(Math.round(p.upliftOrders || 0))} · {formatNpr(p.upliftSales)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-emerald-800/70">—</p>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-rose-800">Underperformers</h4>
            <div className="bi-slim-scroll max-h-40 overflow-y-auto">
              {underperformers.length ? (
                <ul className="space-y-2 text-sm text-rose-950">
                  {underperformers.map((p) => (
                    <li key={`${p.productId}-${p.enteredOn}`} className="flex items-start justify-between gap-2">
                      <span>{p.productName}</span>
                      <span className="shrink-0 font-semibold">
                        {formatNumber(Math.round(p.upliftOrders || 0))} · {formatNpr(p.upliftSales)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-rose-800/70">—</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PromoTracker;
